/**
 * Retouren v3 (sessie 172): de knoppen van het dossier.
 *
 * Eén oranje hoofdknop, altijd `knoppen.hoofd.label`, met eronder in een
 * zin wat er na akkoord gebeurt. Daarnaast alleen de sleutels die de
 * server in `naast` en `meer` zet. Telefoon: hoofdknop vast onderaan,
 * de rest onder "Meer: ...".
 *
 * Er gaat pas iets naar buiten bij `akkoord`, en dat vraagt eerst een
 * bevestiging in de pagina. Nooit window.confirm of alert.
 *
 * Sessie 186: onder Meer staat bij een lopende retour ook "Afgerond buiten
 * het portaal" (reden kiezen, dan bevestigen; geen mail, label of geld).
 */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { adminRetourenApi, apiClient, vertaalApiFout } from "@/lib/api";
import { openPdf } from "@/lib/pdf-openen";
import {
  retourenV3Api,
  type RetourV3Dossier,
  type RetourV3KnopSleutel,
} from "@/lib/retouren-v3-api";
import { werkplekApi, type WerkplekActie } from "@/lib/werkplek-api";

import { AfrondenBuitenPortaal } from "./AfrondenBuitenPortaal";
import { BUITEN_PORTAAL_LABEL, kanBuitenPortaal } from "./buitenPortaal";
import { KoppelKlant } from "./KoppelKlant";
import "./dossier-v3.css";

const KNOP_LABEL: Record<RetourV3KnopSleutel, string> = {
  label_opnieuw: "Label opnieuw sturen",
  afwijzen: "Afwijzen",
  nasturen: "Nasturen via Cenfil",
  kortingscode: "Kortingscode",
  case_vervoerder: "Case bij de vervoerder",
  melding_leverancier: "Melding aan de leverancier",
  pakbon: "Pakbon",
};

/** Het woord in "Meer: ..." op de telefoon. */
const KORT_WOORD: Record<RetourV3KnopSleutel, string> = {
  label_opnieuw: "label",
  pakbon: "pakbon",
  afwijzen: "afwijzen",
  nasturen: "nasturen",
  kortingscode: "kortingscode",
  case_vervoerder: "case vervoerder",
  melding_leverancier: "leverancier",
};

/** Telefoonvolgorde zoals de mock: label, pakbon, afwijzen, dan de rest. */
const TELEFOON_VOLGORDE: RetourV3KnopSleutel[] = [
  "label_opnieuw",
  "pakbon",
  "afwijzen",
  "nasturen",
  "kortingscode",
  "case_vervoerder",
  "melding_leverancier",
];

/** Knop naar soort in de actielaag (`POST /api/werkplek/acties`). */
const ACTIESOORT: Partial<Record<RetourV3KnopSleutel, string>> = {
  nasturen: "nasturen",
  kortingscode: "kortingscode",
  case_vervoerder: "case_tap",
  melding_leverancier: "leverancier_melden",
};

type Paneel =
  | { soort: "akkoord" }
  | { soort: "afwijzen" }
  | { soort: "koppel" }
  | { soort: "label_opnieuw" }
  | { soort: "label_maken" }
  | { soort: "heropenen" }
  | { soort: "buiten_portaal" }
  | { soort: "actie"; label: string; actie: WerkplekActie };

async function heropen(
  retourId: string,
  invoer: { bestand: File | null; notitie: string },
): Promise<void> {
  // Dezelfde route als RetourVerloop (sessie 171).
  const fd = new FormData();
  if (invoer.bestand) fd.append("bestand", invoer.bestand, invoer.bestand.name);
  if (invoer.notitie.trim()) fd.append("notitie", invoer.notitie.trim());
  await apiClient.post(
    `/api/werkplek/retouren/${encodeURIComponent(retourId)}/heropenen`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

function uitkomstTekst(actie: WerkplekActie): string {
  if (actie.droog)
    return "Klaargezet, maar nog niet doorgevoerd: deze actie draait droog.";
  if (actie.foutmelding) return `Mislukt: ${actie.foutmelding}`;
  return "Uitgevoerd. De regel staat bij de zaak.";
}

interface Props {
  dossier: RetourV3Dossier;
  smal: boolean;
  /** Het antwoord van een v3-schrijfroute: in de cache en door. */
  opNieuw: (dossier: RetourV3Dossier) => void;
  /** Na een bestaande route zonder dossier-antwoord: opnieuw ophalen. */
  verversen: () => void;
  /** De zaak van deze retour; maakt hem aan als hij er nog niet is. */
  zorgZaak: () => Promise<string>;
  /** Telefoon: de knop Mail naast de hoofdknop. */
  onMail?: () => void;
}

export function DossierKnoppen({
  dossier,
  smal,
  opNieuw,
  verversen,
  zorgZaak,
  onMail,
}: Props) {
  const retourId = dossier.retour_id;
  const hoofd = dossier.knoppen.hoofd;
  const naast = dossier.knoppen.naast.filter((s) => s !== hoofd?.sleutel);
  const meer = dossier.knoppen.meer.filter(
    (s) => s !== hoofd?.sleutel && !naast.includes(s),
  );
  const buitenPortaal = kanBuitenPortaal(dossier.groep, dossier.stand_code);
  const [paneel, setPaneel] = useState<Paneel | null>(null);
  const [meerOpen, setMeerOpen] = useState(false);
  const [reden, setReden] = useState("");
  const [bestand, setBestand] = useState<File | null>(null);
  const [notitie, setNotitie] = useState("");
  const [melding, setMelding] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const meerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!meerOpen) return;
    function buiten(e: MouseEvent) {
      if (meerRef.current?.contains(e.target as Node)) return;
      setMeerOpen(false);
    }
    function ontsnap(e: KeyboardEvent) {
      if (e.key === "Escape") setMeerOpen(false);
    }
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", ontsnap);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", ontsnap);
    };
  }, [meerOpen]);

  function klaar(nieuw: RetourV3Dossier, tekst: string | null = null) {
    setPaneel(null);
    setFout(null);
    setReden("");
    setMelding(tekst);
    opNieuw(nieuw);
  }

  const klaarzetten = useMutation({
    mutationFn: (invoer: {
      oplossing: "crediteren_volledig" | "afgewezen";
      reden: string | null;
    }) => retourenV3Api.klaarzetten(retourId, invoer),
    onSuccess: (nieuw) => klaar(nieuw),
    onError: (e) => setFout(vertaalApiFout(e, "Klaarzetten is niet gelukt.")),
  });

  const akkoord = useMutation({
    mutationFn: (actieId: string) => retourenV3Api.akkoord(retourId, actieId),
    onSuccess: (nieuw) => klaar(nieuw),
    onError: (e) =>
      setFout(vertaalApiFout(e, "Het akkoord is niet doorgekomen. Er is niets verstuurd.")),
  });

  const intrekken = useMutation({
    mutationFn: (actieId: string) =>
      retourenV3Api.annuleerKlaarzetten(retourId, actieId),
    onSuccess: (nieuw) => klaar(nieuw, "Klaarzetten ingetrokken."),
    onError: (e) => setFout(vertaalApiFout(e, "Intrekken is niet gelukt.")),
  });

  const mailLabel = useMutation({
    mutationFn: () => adminRetourenApi.mailLabelOpnieuw(retourId),
    onSuccess: () => {
      setPaneel(null);
      setFout(null);
      setMelding(
        dossier.klant_email
          ? `Label opnieuw gemaild aan ${dossier.klant_email}.`
          : "Label opnieuw gemaild.",
      );
      verversen();
    },
    onError: (e) => setFout(vertaalApiFout(e, "Het label is niet gemaild.")),
  });

  const labelMaken = useMutation({
    mutationFn: () => adminRetourenApi.labelOpnieuw(retourId),
    onSuccess: () => {
      setPaneel(null);
      setFout(null);
      setMelding("Nieuw label aangevraagd.");
      verversen();
    },
    onError: (e) => setFout(vertaalApiFout(e, "Het label is niet gemaakt.")),
  });

  const heropenen = useMutation({
    mutationFn: () => heropen(retourId, { bestand, notitie }),
    onSuccess: () => {
      setPaneel(null);
      setFout(null);
      setBestand(null);
      setNotitie("");
      setMelding("Heropend.");
      verversen();
    },
    onError: (e) => setFout(vertaalApiFout(e, "Heropenen is niet gelukt.")),
  });

  const actieVoorstel = useMutation({
    mutationFn: async (sleutel: RetourV3KnopSleutel) => {
      const zaakId = await zorgZaak();
      return werkplekApi.stelActieVoor({
        soort: ACTIESOORT[sleutel] as string,
        zaak_id: zaakId,
        klant_id: dossier.klant_id,
      });
    },
    onSuccess: (actie, sleutel) => {
      setFout(null);
      setPaneel({ soort: "actie", label: KNOP_LABEL[sleutel], actie });
    },
    onError: (e) =>
      setFout(vertaalApiFout(e, "Het voorstel kon niet worden klaargezet.")),
  });

  const actieBevestig = useMutation({
    mutationFn: (actieId: string) => werkplekApi.bevestigActie(actieId),
    onSuccess: (actie) => {
      setPaneel(null);
      setFout(null);
      setMelding(uitkomstTekst(actie));
      verversen();
    },
    onError: () => setFout("De actie is niet uitgevoerd. Probeer het opnieuw."),
  });

  function openPakbon() {
    setFout(null);
    // Synchroon bij de tik: openPdf opent het venster vóór de fetch.
    openPdf(
      () => adminRetourenApi.pakbonPdf(retourId),
      `retourpakbon-${dossier.retournummer}.pdf`,
      "De pakbon kon niet worden geopend",
    ).catch((e: unknown) => setFout(e instanceof Error ? e.message : String(e)));
  }

  function open(nieuw: Paneel) {
    setMelding(null);
    setFout(null);
    setMeerOpen(false);
    setPaneel(nieuw);
  }

  function opKnop(sleutel: RetourV3KnopSleutel) {
    if (sleutel === "pakbon") {
      setMeerOpen(false);
      openPakbon();
      return;
    }
    if (sleutel === "label_opnieuw") return open({ soort: "label_opnieuw" });
    if (sleutel === "afwijzen") return open({ soort: "afwijzen" });
    setMelding(null);
    setMeerOpen(false);
    actieVoorstel.mutate(sleutel);
  }

  function opHoofd() {
    if (!hoofd) return;
    setMelding(null);
    setFout(null);
    switch (hoofd.sleutel) {
      case "terugbetalen_klaarzetten":
        klaarzetten.mutate({ oplossing: "crediteren_volledig", reden: null });
        return;
      case "akkoord":
        open({ soort: "akkoord" });
        return;
      case "koppel_klant":
        open({ soort: "koppel" });
        return;
      case "label_opnieuw":
        open({ soort: "label_opnieuw" });
        return;
      case "label_maken":
        open({ soort: "label_maken" });
        return;
      case "heropenen":
        open({ soort: "heropenen" });
        return;
    }
  }

  const hoofdBezig = klaarzetten.isPending || akkoord.isPending;

  function knop(sleutel: RetourV3KnopSleutel) {
    return (
      <button
        key={sleutel}
        type="button"
        className="rv3d-knop"
        data-knop={sleutel}
        disabled={sleutel !== "pakbon" && actieVoorstel.isPending}
        onClick={() => opKnop(sleutel)}
      >
        {KNOP_LABEL[sleutel]}
      </button>
    );
  }

  const hoofdknop = hoofd ? (
    <button
      type="button"
      className="rv3d-hoofdknop"
      data-testid="rv3d-hoofdknop"
      data-sleutel={hoofd.sleutel}
      disabled={hoofdBezig}
      onClick={opHoofd}
    >
      {hoofdBezig ? "Bezig…" : hoofd.label}
    </button>
  ) : null;

  const intrekknop =
    hoofd?.sleutel === "akkoord" && hoofd.actie_id ? (
      <button
        type="button"
        className="rv3d-knop rv3d-knop-klein"
        data-testid="rv3d-intrekken"
        disabled={intrekken.isPending}
        onClick={() => intrekken.mutate(hoofd.actie_id as string)}
      >
        {intrekken.isPending ? "Bezig…" : "Klaarzetten intrekken"}
      </button>
    ) : null;

  const naAkkoord = hoofd?.na_akkoord ? (
    <div className="rv3d-na-akkoord" data-testid="rv3d-na-akkoord">
      {hoofd.na_akkoord}
    </div>
  ) : null;

  const paneelInhoud = paneel ? (
    paneel.soort === "akkoord" && hoofd?.actie_id ? (
      <div className="rv3d-paneel" data-testid="rv3d-akkoord-bevestig">
        <p>
          <strong>{hoofd.label}?</strong>
        </p>
        <p className="rv3d-melding">{hoofd.na_akkoord}</p>
        {hoofd.mail_tekst ? (
          <div className="rv3d-mailvoorbeeld" data-testid="rv3d-akkoord-mail">
            <div className="rv3d-veldlabel">Mail aan de klant</div>
            {hoofd.mail_onderwerp ? <p><strong>{hoofd.mail_onderwerp}</strong></p> : null}
            <p className="rv3d-mailtekst">{hoofd.mail_tekst}</p>
          </div>
        ) : null}
        <div className="rv3d-paneel-knoppen">
          <button
            type="button"
            className="rv3d-hoofdknop"
            data-testid="rv3d-akkoord-ja"
            disabled={akkoord.isPending}
            onClick={() => akkoord.mutate(hoofd.actie_id as string)}
          >
            {akkoord.isPending ? "Bezig…" : "Ja, akkoord"}
          </button>
          <button type="button" className="rv3d-knop" onClick={() => setPaneel(null)}>
            Nog niet
          </button>
        </div>
      </div>
    ) : paneel.soort === "afwijzen" ? (
      <form
        className="rv3d-paneel"
        data-testid="rv3d-afwijzen"
        onSubmit={(e) => {
          e.preventDefault();
          if (reden.trim())
            klaarzetten.mutate({ oplossing: "afgewezen", reden: reden.trim() });
        }}
      >
        <label className="rv3d-veldlabel">
          Reden van afwijzen
          <textarea
            className="rv3d-veld"
            rows={2}
            data-testid="rv3d-afwijzen-reden"
            value={reden}
            onChange={(e) => setReden(e.target.value)}
          />
        </label>
        <div className="rv3d-paneel-knoppen">
          <button
            type="submit"
            className="rv3d-knop rv3d-knop-klein"
            data-testid="rv3d-afwijzen-klaarzetten"
            disabled={!reden.trim() || klaarzetten.isPending}
          >
            {klaarzetten.isPending ? "Bezig…" : "Afwijzing klaarzetten"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            onClick={() => setPaneel(null)}
          >
            Annuleren
          </button>
        </div>
      </form>
    ) : paneel.soort === "koppel" ? (
      <KoppelKlant
        dossier={dossier}
        opNieuw={opNieuw}
        onSluiten={() => setPaneel(null)}
      />
    ) : paneel.soort === "label_opnieuw" ? (
      <div className="rv3d-paneel" data-testid="rv3d-label-bevestig">
        <p>
          Label opnieuw mailen
          {dossier.klant_email ? ` aan ${dossier.klant_email}` : " aan de klant"}?
        </p>
        <div className="rv3d-paneel-knoppen">
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            data-testid="rv3d-label-ja"
            disabled={mailLabel.isPending}
            onClick={() => mailLabel.mutate()}
          >
            {mailLabel.isPending ? "Bezig…" : "Ja, mail het label"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            onClick={() => setPaneel(null)}
          >
            Nog niet
          </button>
        </div>
      </div>
    ) : paneel.soort === "label_maken" ? (
      <div className="rv3d-paneel" data-testid="rv3d-labelmaken-bevestig">
        <p>Een nieuw label maken bij de vervoerder?</p>
        <div className="rv3d-paneel-knoppen">
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            data-testid="rv3d-labelmaken-ja"
            disabled={labelMaken.isPending}
            onClick={() => labelMaken.mutate()}
          >
            {labelMaken.isPending ? "Bezig…" : "Ja, maak het label"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            onClick={() => setPaneel(null)}
          >
            Nog niet
          </button>
        </div>
      </div>
    ) : paneel.soort === "heropenen" ? (
      <form
        className="rv3d-paneel"
        data-testid="rv3d-heropenen"
        onSubmit={(e) => {
          e.preventDefault();
          if (bestand || notitie.trim()) heropenen.mutate();
        }}
      >
        <label className="rv3d-veldlabel">
          Afgiftebewijs, foto of PDF
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setBestand(e.target.files?.[0] ?? null)}
          />
        </label>
        <label className="rv3d-veldlabel">
          Of een notitie met het bewijs
          <textarea
            className="rv3d-veld"
            rows={2}
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
          />
        </label>
        <div className="rv3d-paneel-knoppen">
          <button
            type="submit"
            className="rv3d-knop rv3d-knop-klein"
            disabled={(!bestand && !notitie.trim()) || heropenen.isPending}
          >
            {heropenen.isPending ? "Bezig…" : "Heropenen"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            onClick={() => setPaneel(null)}
          >
            Annuleren
          </button>
        </div>
      </form>
    ) : paneel.soort === "buiten_portaal" ? (
      <AfrondenBuitenPortaal
        retourId={retourId}
        retournummer={dossier.retournummer}
        onKlaar={(nieuw) =>
          klaar(
            nieuw,
            "Afgerond buiten het portaal. Er is geen mail en geen geld via het portaal gegaan.",
          )
        }
        onSluiten={() => setPaneel(null)}
      />
    ) : paneel.soort === "actie" ? (
      <div className="rv3d-paneel" data-testid="rv3d-actie-bevestig">
        <p>
          {paneel.actie.bevestigtekst ?? `${paneel.label} voor deze retour?`}
        </p>
        <div className="rv3d-paneel-knoppen">
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            data-testid="rv3d-actie-ja"
            disabled={actieBevestig.isPending}
            onClick={() => actieBevestig.mutate(paneel.actie.id)}
          >
            {actieBevestig.isPending ? "Bezig…" : "Ja, doe het"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            onClick={() => {
              void werkplekApi.annuleerActie(paneel.actie.id).catch(() => undefined);
              setPaneel(null);
            }}
          >
            Annuleren
          </button>
        </div>
      </div>
    ) : null
  ) : null;

  const terugmelding = (
    <>
      {melding ? (
        <div className="rv3d-melding" role="status" data-testid="rv3d-knopmelding">
          {melding}
        </div>
      ) : null}
      {fout ? (
        <div className="rv3d-fout" role="alert">
          {fout}
        </div>
      ) : null}
    </>
  );

  const buitenPortaalKnop = (
    <button
      key="buiten_portaal"
      type="button"
      className="rv3d-knop"
      data-knop="buiten_portaal"
      onClick={() => open({ soort: "buiten_portaal" })}
    >
      {BUITEN_PORTAAL_LABEL}
    </button>
  );

  if (smal) {
    const alles = TELEFOON_VOLGORDE.filter(
      (s) => naast.includes(s) || meer.includes(s),
    );
    const woorden = alles.map((s) => KORT_WOORD[s]);
    if (buitenPortaal) woorden.push("afronden");
    return (
      <>
        {woorden.length > 0 ? (
          <details className="rv3d-meer-details" data-testid="rv3d-meer-details">
            <summary>Meer: {woorden.join(", ")}</summary>
            <div className="rv3d-meer-details-knoppen">
              {alles.map(knop)}
              {buitenPortaal ? buitenPortaalKnop : null}
            </div>
          </details>
        ) : null}
        <div className="rv3d-voet" data-testid="rv3d-voet">
          {paneelInhoud}
          {terugmelding}
          <div className="rv3d-voet-rij">
            {hoofdknop}
            {onMail ? (
              <button type="button" className="rv3d-mailknop" onClick={onMail}>
                Mail
              </button>
            ) : null}
          </div>
          {intrekknop ? <div>{intrekknop}</div> : null}
          {naAkkoord}
        </div>
      </>
    );
  }

  return (
    <div className="rv3d-knoppenblok">
      <div className="rv3d-knoppen" data-testid="rv3d-knoppen">
        {hoofdknop}
        {intrekknop}
        {naast.map(knop)}
        {meer.length > 0 || buitenPortaal ? (
          <div className="rv3d-meer" ref={meerRef}>
            <button
              type="button"
              className="rv3d-knop"
              data-testid="rv3d-meer"
              aria-expanded={meerOpen}
              aria-haspopup="menu"
              onClick={() => setMeerOpen((aan) => !aan)}
            >
              Meer
            </button>
            {meerOpen ? (
              <div className="rv3d-meerlijst" role="menu">
                {meer.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="menuitem"
                    data-knop={s}
                    onClick={() => opKnop(s)}
                  >
                    {KNOP_LABEL[s]}
                  </button>
                ))}
                {buitenPortaal ? (
                  <button
                    type="button"
                    role="menuitem"
                    data-knop="buiten_portaal"
                    onClick={() => open({ soort: "buiten_portaal" })}
                  >
                    {BUITEN_PORTAAL_LABEL}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {naAkkoord}
      {paneelInhoud}
      {terugmelding}
    </div>
  );
}
